/**
 * Sistema colori aree — sync DB + cache localStorage.
 *
 * I default sono in `constants/areas.js`. Questo modulo:
 *  1. Al boot legge gli override dal localStorage e MUTA l'oggetto AREE
 *     così tutti i `AREE[key]` esistenti vedono i colori personalizzati.
 *  2. `bootstrapAreasFromServer()` carica le aree utente da `/api/aree` e le
 *     applica (chiamato al login dall'AppLayout).
 *  3. Espone `setAreaColor`, `addArea`, `removeArea`, `getAllAreasMerged`,
 *     `listKnownAreas` con persistenza DB + cache locale.
 *  4. Dopo ogni modifica emette `qp:areas-changed` per i consumer reattivi.
 */
import { AREE, AREE_ORDER } from '../constants/areas';
import * as apiClient from './api';

const STORAGE_KEY = 'qp.area_colors';
const STORAGE_LABELS_KEY = 'qp.area_labels';

/** Palette di base proposta dal picker. Hex. */
export const COLOR_PALETTE = [
  '#E5484D', // rosso (default business)
  '#DC2626', // rosso scuro
  '#F97316', // arancio (default personale)
  '#F59E0B', // ambra
  '#EAB308', // oro
  '#16A34A', // verde
  '#0EA5E9', // celeste
  '#2563EB', // blu (default lavoro)
  '#7C3AED', // viola
  '#DB2777', // magenta
  '#475569', // grigio-blu (default salute)
  '#171717', // ink
];

const DEFAULT_KEYS = new Set(AREE_ORDER);

function safeRead(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return {};
    const obj = JSON.parse(raw);
    return obj && typeof obj === 'object' ? obj : {};
  } catch (err) {
    console.warn(`[areaColors] safeRead(${key}) fallito:`, err);
    return {};
  }
}

function safeWrite(key, obj) {
  try {
    localStorage.setItem(key, JSON.stringify(obj));
  } catch (err) {
    console.warn(`[areaColors] safeWrite(${key}) fallito (quota?):`, err);
  }
}

function loadColorOverrides() {
  return safeRead(STORAGE_KEY);
}

function saveColorOverrides(overrides) {
  safeWrite(STORAGE_KEY, overrides);
}

function loadLabelOverrides() {
  return safeRead(STORAGE_LABELS_KEY);
}

function saveLabelOverrides(overrides) {
  safeWrite(STORAGE_LABELS_KEY, overrides);
}

/** Mutazione in-place di `AREE` per applicare gli override. */
function applyOverrides(colorOverrides, labelOverrides) {
  for (const [key, color] of Object.entries(colorOverrides || {})) {
    if (!color || typeof color !== 'string') continue;
    if (AREE[key]) {
      AREE[key] = { ...AREE[key], color };
    } else {
      AREE[key] = {
        label: key.charAt(0).toUpperCase() + key.slice(1),
        color,
        textClass: '',
      };
    }
  }
  for (const [key, label] of Object.entries(labelOverrides || {})) {
    if (!label || typeof label !== 'string') continue;
    if (AREE[key]) {
      AREE[key] = { ...AREE[key], label };
    } else {
      AREE[key] = { label, color: '#525252', textClass: '' };
    }
  }
}

// Bootstrap dal localStorage (subito, prima del primo render)
applyOverrides(loadColorOverrides(), loadLabelOverrides());

// Stato runtime per ordine + id DB
const _runtime = {
  order: [...AREE_ORDER],
  ids: {}, // key -> uuid (se persistito su DB; assente per default puro)
};

function emit(detail) {
  window.dispatchEvent(new CustomEvent('qp:areas-changed', { detail: detail || {} }));
}

/**
 * Carica aree da server e applica.
 * Chiamato dall'AppLayout subito dopo login.
 */
export async function bootstrapAreasFromServer() {
  try {
    const res = await apiClient.fetchAree();
    const items = res?.items || [];
    if (!items.length) return false;
    const colorOv = {};
    const labelOv = {};
    const ids = {};
    const order = [];
    for (const it of items) {
      const k = (it.key || '').toLowerCase();
      if (!k) continue;
      colorOv[k] = it.color;
      labelOv[k] = it.label;
      if (it.id) ids[k] = it.id;
      order.push(k);
    }
    applyOverrides(colorOv, labelOv);
    saveColorOverrides({ ...loadColorOverrides(), ...colorOv });
    saveLabelOverrides({ ...loadLabelOverrides(), ...labelOv });
    _runtime.order = order.length ? order : _runtime.order;
    _runtime.ids = ids;
    emit({ source: 'server' });
    return true;
  } catch (err) {
    // table missing o offline: si rimane su default/cache
    console.warn('[areaColors] hydrateFromServer fallito (offline/no-table):', err);
    return false;
  }
}

/** Cambia colore (DB + cache). Ritorna {ok, persisted, error?}. */
export async function setAreaColor(key, color) {
  if (!key) return { ok: false, persisted: false, error: 'key mancante' };
  const normalized = String(key).toLowerCase().trim();
  const prevColor = AREE[normalized]?.color;
  const overrides = loadColorOverrides();
  overrides[normalized] = color;
  saveColorOverrides(overrides);
  applyOverrides({ [normalized]: color }, {});
  emit({ key: normalized, color });
  try {
    await apiClient.updateArea(normalized, { color });
    return { ok: true, persisted: true };
  } catch (err) {
    const detail = err?.response?.data?.detail || err?.message || 'errore persistenza';
    // Rollback: ripristina il colore precedente (DB è source of truth)
    if (prevColor) {
      const ov = loadColorOverrides();
      ov[normalized] = prevColor;
      saveColorOverrides(ov);
      applyOverrides({ [normalized]: prevColor }, {});
      emit({ key: normalized, color: prevColor, rolledBack: true });
    }
    return { ok: false, persisted: false, error: detail };
  }
}

/** Aggiunge una nuova area (DB + cache). Ritorna {ok, persisted, key?, error?}. */
export async function addArea(key, label, color) {
  if (!label || !color) return { ok: false, persisted: false, error: 'label e colore obbligatori' };
  const normalized = String(key || label).toLowerCase().trim().replace(/[^a-z0-9_]/g, '');
  if (!normalized) return { ok: false, persisted: false, error: 'key non valida' };
  // Persistenza DB *prima* di mutare lo stato locale (rollback più semplice)
  try {
    const res = await apiClient.createArea({ key: normalized, label, color });
    AREE[normalized] = { label, color, textClass: '' };
    saveColorOverrides({ ...loadColorOverrides(), [normalized]: color });
    saveLabelOverrides({ ...loadLabelOverrides(), [normalized]: label });
    if (!_runtime.order.includes(normalized)) _runtime.order.push(normalized);
    if (res?.data?.id) _runtime.ids[normalized] = res.data.id;
    emit({ key: normalized, color, label, added: true });
    return { ok: true, persisted: true, key: normalized };
  } catch (err) {
    const detail = err?.response?.data?.detail || err?.message || 'errore creazione';
    return { ok: false, persisted: false, error: detail };
  }
}

/** Rimuove un'area custom (non si possono rimuovere i default). Ritorna {ok, persisted, error?}. */
export async function removeArea(key) {
  const normalized = String(key || '').toLowerCase().trim();
  if (!normalized) return { ok: false, persisted: false, error: 'key mancante' };
  if (DEFAULT_KEYS.has(normalized)) {
    return { ok: false, persisted: false, error: 'Le aree di default non possono essere eliminate' };
  }
  // DB first
  try {
    await apiClient.deleteArea(normalized);
  } catch (err) {
    const detail = err?.response?.data?.detail || err?.message || 'errore eliminazione';
    return { ok: false, persisted: false, error: detail };
  }
  // Local cleanup solo se DB ok
  delete AREE[normalized];
  const co = loadColorOverrides();
  delete co[normalized];
  saveColorOverrides(co);
  const lo = loadLabelOverrides();
  delete lo[normalized];
  saveLabelOverrides(lo);
  _runtime.order = _runtime.order.filter((k) => k !== normalized);
  emit({ key: normalized, removed: true });
  return { ok: true, persisted: true };
}

export function getAllAreasMerged() {
  return AREE;
}

/**
 * Riordina le aree secondo `nextOrder` (array di key). Persiste su DB
 * settando `ordine` su ciascuna area (i default mancanti vengono creati come
 * row "promosse" via PATCH). Ritorna {ok, persisted, error?}.
 */
export async function reorderAreas(nextOrder) {
  if (!Array.isArray(nextOrder) || nextOrder.length === 0) {
    return { ok: false, persisted: false, error: 'ordine vuoto' };
  }
  const prev = [..._runtime.order];
  _runtime.order = nextOrder.filter((k) => AREE[k]);
  emit({ reordered: true });
  // Persistenza DB: PATCH ordine per ogni key
  try {
    await Promise.all(
      nextOrder.map((key, idx) => apiClient.updateArea(key, { ordine: idx })),
    );
    return { ok: true, persisted: true };
  } catch (err) {
    const detail = err?.response?.data?.detail || err?.message || 'errore riordino';
    // Rollback ordine locale
    _runtime.order = prev;
    emit({ reordered: true, rolledBack: true });
    return { ok: false, persisted: false, error: detail };
  }
}

export function listKnownAreas() {
  const keys = Object.keys(AREE);
  const ordered = [];
  for (const k of _runtime.order) {
    if (keys.includes(k) && !ordered.includes(k)) ordered.push(k);
  }
  for (const k of AREE_ORDER) {
    if (keys.includes(k) && !ordered.includes(k)) ordered.push(k);
  }
  for (const k of keys) {
    if (!ordered.includes(k)) ordered.push(k);
  }
  return ordered;
}

/** Registra un'area localmente (utility legacy — non sincronizza DB). */
export function registerArea(key, color, label) {
  if (!key) return;
  const normalized = String(key).toLowerCase().trim();
  if (!AREE[normalized]) {
    AREE[normalized] = {
      label: label || (normalized.charAt(0).toUpperCase() + normalized.slice(1)),
      color: color || '#525252',
      textClass: '',
    };
  }
  if (color) {
    const overrides = loadColorOverrides();
    overrides[normalized] = color;
    saveColorOverrides(overrides);
    applyOverrides({ [normalized]: color }, {});
    emit({ key: normalized, color });
  }
}
